import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { TicketedEventTicket } from '@Types/ticketedEvents';
import { stableStringify } from '@Utils/searchParams';

type TicketedEventTicketResponse = {
  data: TicketedEventTicket[];
  meta: Record<string, any>;
};

type DefaultError = unknown;

type UseTicketedEventTicketParams<
  S extends Record<string, any> = Record<string, any>,
> = {
  searchParams?: S;
  enabled?: boolean;
};

export function useTicketedEventTicket<
  TData = TicketedEventTicketResponse,
  TError = DefaultError,
>(
  { searchParams, enabled = true }: UseTicketedEventTicketParams = {},
  options?: Omit<
    UseQueryOptions<TicketedEventTicketResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const stableParams = useMemo(
    () => stableStringify(searchParams),
    [searchParams],
  );

  return useQuery<TicketedEventTicketResponse, TError, TData>({
    queryKey: ['ticketedEventTicket', stableParams],
    enabled: enabled,
    queryFn: async () => {
      const response = await service.getTicketedEventTickets(searchParams);
      return response;
    },
    ...(options as any),
  });
}
